import { createTheme, Theme, ThemeProvider } from '@mui/material';
import { getExpandedRowModel, getGroupedRowModel } from '@tanstack/react-table';
import {
  MaterialReactTable,
  MRT_Cell,
  MRT_Localization,
  MRT_Row,
  MRT_TableInstance,
  MRT_TableOptions,
  MRT_TableState,
} from 'material-react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Type is from the lib
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RowActionsRenderer<TData extends Record<string, any>> = ({
  cell,
  row,
  table,
}: {
  cell: MRT_Cell<TData>;
  row: MRT_Row<TData>;
  table: MRT_TableInstance<TData>;
}) => React.ReactNode;

const createTableTheme = (theme: Theme): Theme =>
  createTheme({
    ...theme,
    palette: { ...theme.palette, background: { default: theme.palette.background.paper } },
    components: {
      ...theme.components,
      MuiTableCell: {
        styleOverrides: {
          head: { color: theme.palette.text.secondary, fontSize: 14, textTransform: 'uppercase', fontWeight: '500 !important' },
        },
      },
    },
  });

const PAPER_PROPS = { elevation: 0 };
const TABLE_CONTAINER_PROPS = { sx: { overflowX: 'auto' } };

function isMrtCopyShortcut(event: React.KeyboardEvent): boolean {
  if (event.key !== 'c' && event.key !== 'C') {
    return false;
  }
  const isApplePlatform = /mac|iphone|ipad|ipod/.test(navigator.platform.toLowerCase());
  return isApplePlatform ? event.metaKey : event.ctrlKey;
}

/**
 * MRT copies cell.getValue() on Ctrl/Cmd+C. That becomes "[object Object]" for React cells
 * and the accessor value (e.g. lastName) when the Cell shows more than that.
 * Stop MRT's handler so the browser copies the highlighted text; if nothing is selected, copy the visible cell text.
 */
function preserveNativeCellCopy(event: React.KeyboardEvent<HTMLTableCellElement>): void {
  if (!isMrtCopyShortcut(event)) {
    return;
  }

  const selectedText = window.getSelection()?.toString();
  if (selectedText) {
    event.stopPropagation();
    return;
  }

  const visibleText = event.currentTarget.innerText?.trim();
  if (!visibleText) {
    return;
  }

  event.stopPropagation();
  event.preventDefault();
  void navigator.clipboard.writeText(visibleText);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ThemedMaterialReactTable = <TData extends Record<string, any> = {}>(props: MRT_TableOptions<TData>): React.JSX.Element => {
  const { t } = useTranslation('common');

  const state = useMemo<Partial<MRT_TableState<TData>>>(() => ({ ...props.state, density: 'compact' }), [props.state]);
  const localization = useMemo(
    () => ({ ...(t('materialReactTableLocale', { returnObjects: true }) as MRT_Localization), ...props.localization }),
    [t, props.localization],
  );

  // Merge the default overflow fix with any caller-provided container props so it is preserved rather than replaced by the spread.
  const muiTableContainerProps = useMemo(() => {
    const containerProps = typeof props.muiTableContainerProps === 'object' ? props.muiTableContainerProps : undefined;
    const callerSx = containerProps?.sx;
    return { ...containerProps, sx: [TABLE_CONTAINER_PROPS.sx, ...(Array.isArray(callerSx) ? callerSx : [callerSx])] };
  }, [props.muiTableContainerProps]);

  const muiTableBodyCellProps = useMemo<MRT_TableOptions<TData>['muiTableBodyCellProps']>(() => {
    const callerProps = props.muiTableBodyCellProps;
    return args => {
      const resolved = typeof callerProps === 'function' ? callerProps(args) : callerProps;
      return {
        ...resolved,
        onKeyDown: (event: React.KeyboardEvent<HTMLTableCellElement>) => {
          resolved?.onKeyDown?.(event);
          preserveNativeCellCopy(event);
        },
      };
    };
  }, [props.muiTableBodyCellProps]);

  return (
    <ThemeProvider theme={createTableTheme}>
      <MaterialReactTable<TData>
        muiTablePaperProps={PAPER_PROPS}
        getExpandedRowModel={getExpandedRowModel()}
        getGroupedRowModel={getGroupedRowModel()}
        {...props}
        muiTableBodyCellProps={muiTableBodyCellProps}
        muiTableContainerProps={muiTableContainerProps}
        displayColumnDefOptions={{
          'mrt-row-actions': {
            header: '',
          },
          ...props.displayColumnDefOptions,
        }}
        state={state}
        localization={localization}
      />
    </ThemeProvider>
  );
};
